import React, { useEffect, useRef } from 'react';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { LabelledDropdown, ControlledDropdownMenu } from './StyledDropdown';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SlDropdownMenuOption } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlMenu';
import { useWatch, useFormContext } from 'react-hook-form';
import { StyledSettingInput } from './StyledSettingInput';
import {
  BRAND_NAME_FORMATTING,
  APPROVED_SUBSTITUTIONS,
  AVOID_CROSS_CATEGORY_COMPARISONS,
  APPROVED_NUTRIENT_RELATED_PHRASES,
  AVOID_SPECIFIC_CLAIM_LANGUAGE,
  APPROVED_ON_PACKAGE_LANGUAGE,
  APPROVED_QUALIFIER_PHRASES_BEFORE_CLAIMS,
  APPROVED_PRODUCT_ATTRIBUTE_AND_DISCLAIMER_TEXT,
  AVOID_RESTRICTED_TEXT_FOR_CLAIMS
} from '../types/profileTypes';
import { useFormattedData } from '../hooks/useFormattedData';
import { useAppSelector } from 'src/utils/Hooks';
import { ControlledInput } from './ControlledInput';

// Reusable wrapper components for consistent styling
interface ConditionsWrapperProps {
  children: React.ReactNode;
  title?: string;
}

const ConditionsWrapper: React.FC<ConditionsWrapperProps> = ({ children, title = 'Conditions' }) => {
  return (
    <Flex flexDirection="column" gap={8}>
      <Text variant="h7" fontWeight={500}>
        {title}
      </Text>
      {children}
    </Flex>
  );
};

interface ConditionsRowProps {
  children: React.ReactNode;
  marginTop?: string;
  marginBottom?: string;
  gap?: number;
}

const ConditionsRow: React.FC<ConditionsRowProps> = ({ children, marginTop, marginBottom, gap = 8 }) => {
  return (
    <Flex alignItems="center" gap={gap} sx={{ marginTop: marginTop, marginBottom: marginBottom }}>
      {children}
    </Flex>
  );
};

// Shared styles for LabelledDropdown components
const LABELLED_DROPDOWN_MENU_STYLES = {
  maxHeight: '167.5px',
  border: '1px solid #E8E8ED',
  '& .MuiTypography-root': {
    fontWeight: '400 !important'
  }
};

const LABELLED_DROPDOWN_BUTTON_PROPS = {
  buttonSx: {
    width: 'fit-content',
    '& .MuiTypography-root': {
      fontWeight: '400 !important',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      WebkitLineClamp: 1,
      WebkitBoxOrient: 'vertical',
      display: '-webkit-box',
      textAlign: 'start'
    }
  }
};

interface BrandNameFormattingProps {
  index: number;
}

// Use the BaseFormProfile type from profileTypes.ts
const BrandNameFormatting: React.FC<BrandNameFormattingProps> = ({ index }) => {
  const { control } = useFormContext();
  const entityName = useAppSelector((state) => state.user.config.vendor?.CompanyName);

  const formatOptions: SlDropdownMenuOption[] = [
    { id: 'in title casing', label: 'in title casing' },
    { id: 'with all capital letters', label: 'with all capital letters' },
    { id: 'with all lowercase letters', label: 'with all lowercase letters' },
    { id: 'in custom text', label: 'in custom text' }
  ];

  const conditionOptions: SlDropdownMenuOption[] = [
    { id: 'always', label: 'always' },
    { id: 'never', label: 'never' }
  ];

  const brandNames = useFormattedData('brands') as SlDropdownMenuOption[];

  // Watch the format value
  const format = useWatch({
    control,
    name: `parameters.format`
  });

  return (
    <Flex flexDirection="column" gap={32}>
      <LabelledDropdown
        name={`parameters.brandName`}
        label="Brand Selection"
        options={brandNames}
        showTooltip={false}
        menuStyles={LABELLED_DROPDOWN_MENU_STYLES}
        stacklineButtonProps={LABELLED_DROPDOWN_BUTTON_PROPS}
      />
      <ConditionsWrapper>
        <ConditionsRow marginTop="-4px">
          <Text
            variant="body2"
            fontWeight={400}
            sx={{
              whiteSpace: 'nowrap'
            }}
          >
            For all selected products, the brand should
          </Text>
          <ControlledDropdownMenu name={`parameters.condition`} options={conditionOptions} defaultValue="always" />
          <Text
            variant="body2"
            fontWeight={400}
            sx={{
              whiteSpace: 'nowrap'
            }}
          >
            be written:
          </Text>
        </ConditionsRow>
        <ConditionsRow>
          <ControlledDropdownMenu
            name={`parameters.format`}
            options={formatOptions}
            defaultValue="with all capital letters"
          />
          <Text
            variant="body2"
            fontWeight={400}
            sx={{
              whiteSpace: 'nowrap'
            }}
          >
            .
          </Text>
        </ConditionsRow>
      </ConditionsWrapper>
      {format === 'in custom text' && (
        <StyledSettingInput
          label="Custom Text"
          placeholder="Enter custom text"
          characterLimit={100}
          name={`parameters.customText`}
          textSx={{
            fontWeight: 500,
            marginTop: '-12px'
          }}
        />
      )}
    </Flex>
  );
};

interface AvoidCrossCategoryComparisonsProps {
  index: number;
}

const AvoidCrossCategoryComparisons: React.FC<AvoidCrossCategoryComparisonsProps> = ({ index }) => {
  const { control, setValue } = useFormContext();
  const prevComparisonTypeRef = useRef<string | null>(null);

  // Define comparison types based on the schema
  const comparisonTypes: SlDropdownMenuOption[] = [
    { id: 'category', label: 'category' },
    { id: 'sub-category', label: 'sub-category' }
  ];

  const comparisonType = useWatch({
    control,
    name: `parameters.comparisonType`
  });

  const categories = useFormattedData(comparisonType) as SlDropdownMenuOption[];

  // Only reset target when comparisonType actually changes, not on every render
  useEffect(() => {
    if (comparisonType && prevComparisonTypeRef.current !== comparisonType && categories.length > 0) {
      setValue(`parameters.target`, categories[0].id);
      prevComparisonTypeRef.current = comparisonType;
    } else if (!prevComparisonTypeRef.current && comparisonType) {
      // Initialize the ref on first render
      prevComparisonTypeRef.current = comparisonType;
    }
  }, [comparisonType, categories, setValue]);

  return (
    <ConditionsWrapper>
      <Text variant="body2">
        For all selected products, avoid making product comparison claims against products in the:
      </Text>
      <ConditionsRow>
        <ControlledDropdownMenu name={`parameters.comparisonType`} options={comparisonTypes} defaultValue="category" />
        <Text variant="body1">:</Text>
        <ControlledDropdownMenu
          name={`parameters.target`}
          options={categories}
          defaultValue={categories[0]?.id}
          menuStyles={LABELLED_DROPDOWN_MENU_STYLES}
          stacklineButtonProps={{
            buttonSx: {
              width: 'fit-content',
              '& .MuiTypography-root': {
                fontWeight: '400 !important'
              }
            }
          }}
        />
        <Text variant="body1">.</Text>
      </ConditionsRow>
    </ConditionsWrapper>
  );
};

interface ApprovedSubstitutionsProps {
  index: number;
}

const ApprovedSubstitutions: React.FC<ApprovedSubstitutionsProps> = ({ index }) => {
  return (
    <ConditionsWrapper>
      <Text variant="body2">When writing new content to describe the product or qualities of the product, use:</Text>
      <ControlledInput
        placeholder="Enter approved text"
        name={`parameters.approvedTerm`}
        textSx={{
          fontWeight: 500
        }}
      />
      <Text
        variant="body2"
        sx={{
          marginTop: '-px'
        }}
      >
        instead of:
      </Text>
      <ControlledInput
        placeholder="Enter restricted text"
        name={`parameters.restrictedTerm`}
        textSx={{
          fontWeight: 500
        }}
      />
    </ConditionsWrapper>
  );
};

interface ApprovedNutrientRelatedPhrasesProps {
  index: number;
}

const ApprovedNutrientRelatedPhrases: React.FC<ApprovedNutrientRelatedPhrasesProps> = ({ index }) => {
  const measurementOptions: SlDropdownMenuOption[] = [
    { id: 'Grams (g)', label: 'Grams (g)' },
    { id: 'Ounces (oz)', label: 'Ounces (oz)' },
    { id: 'Pounds (lb)', label: 'Pounds (lbs)' },
    { id: 'Kilograms (kg)', label: 'Kilograms (kg)' }
  ];

  return (
    <ConditionsWrapper>
      <ConditionsRow>
        <Text
          variant="body2"
          fontWeight={400}
          sx={{
            whiteSpace: 'nowrap',
            letterSpacing: '-0.01em'
          }}
        >
          For all selected products, when mentioning
        </Text>
        <ControlledInput
          placeholder="Enter nutrient text"
          name={`parameters.nutrient`}
          style={{
            width: '144px'
          }}
        />
        <Text
          variant="body2"
          fontWeight={400}
          sx={{
            whiteSpace: 'nowrap',
            letterSpacing: '-0.01em'
          }}
        >
          always use the following statement:
        </Text>
      </ConditionsRow>
      <ConditionsRow marginTop="8px">
        <Text variant="body2" sx={{ marginRight: '-4px' }}>
          &ldquo;
        </Text>
        <ControlledInput
          type="number"
          placeholder="Enter amount"
          name={`parameters.amount`}
          min={0}
          style={{
            width: '120px'
          }}
        />
        <ControlledDropdownMenu name={`parameters.unit`} options={measurementOptions} defaultValue="Grams (g)" />
        <Text variant="body2">of</Text>
        <ControlledInput
          placeholder="Enter nutrient text"
          name={`parameters.nutrient`}
          style={{
            width: '144px'
          }}
        />
        <Text variant="body2">per serving.&rdquo;</Text>
      </ConditionsRow>
    </ConditionsWrapper>
  );
};

interface AvoidSpecificClaimLanguageProps {
  index: number;
}

const AvoidSpecificClaimLanguage: React.FC<AvoidSpecificClaimLanguageProps> = ({ index }) => {
  const claimTypeOptions: SlDropdownMenuOption[] = [
    { id: 'such as', label: 'such as' },
    { id: 'about', label: 'about' }
  ];

  return (
    <ConditionsWrapper>
      <ConditionsRow marginBottom="8px">
        <Text variant="body2">For all selected products, avoid claims</Text>
        <ControlledDropdownMenu name={`parameters.claimType`} options={claimTypeOptions} defaultValue="such as" />
        <Text variant="body2" sx={{ marginLeft: '-4px' }}>
          :
        </Text>
      </ConditionsRow>
      <ControlledInput
        placeholder="Enter custom text (add multiple items by separating them with a semicolon)"
        name={`parameters.topic`}
        multiline
        style={{
          height: '92px'
        }}
      />
    </ConditionsWrapper>
  );
};

interface ApprovedOnPackageLanguageProps {
  index: number;
}

const ApprovedOnPackageLanguage: React.FC<ApprovedOnPackageLanguageProps> = ({ index }) => {
  return (
    <ConditionsWrapper>
      <Text variant="body2" sx={{ marginBottom: '8px' }}>
        When making claims about the selected products, only use the on-package language:
      </Text>
      <ControlledInput
        placeholder="Enter on-package text (add multiple items by separating them with a semicolon)"
        name={`parameters.onPackageText`}
        multiline
        style={{
          height: '92px'
        }}
      />
    </ConditionsWrapper>
  );
};

interface ApprovedQualifierPhrasesBeforeClaimsProps {
  index: number;
}

const ApprovedQualifierPhrasesBeforeClaims: React.FC<ApprovedQualifierPhrasesBeforeClaimsProps> = ({ index }) => {
  return (
    <ConditionsWrapper>
      <Text variant="body2">For all selected products, use:</Text>
      <ControlledInput placeholder="Enter approved text" name={`parameters.approvedPhrase`} />
      <Text variant="body2" sx={{ marginTop: '8px' }}>
        qualifier immediately before the following claim:
      </Text>
      <ControlledInput placeholder="Enter custom claim text" name={`parameters.claim`} />
    </ConditionsWrapper>
  );
};

interface ApprovedProductAttributeAndDisclaimerTextProps {
  index: number;
}

const ApprovedProductAttributeAndDisclaimerText: React.FC<ApprovedProductAttributeAndDisclaimerTextProps> = ({
  index
}) => {
  return (
    <ConditionsWrapper>
      <Text variant="body2">For all selected products, when referring to:</Text>
      <ControlledInput placeholder="Enter product attribute" name={`parameters.productAttribute`} />
      <Text variant="body2" sx={{ marginTop: '8px' }}>
        use:
      </Text>
      <ControlledInput placeholder="Enter approved text" name={`parameters.approvedReplacement`} />
      <Text variant="body2" sx={{ marginTop: '8px' }}>
        instead of:
      </Text>
      <ControlledInput placeholder="Enter restricted text" name={`parameters.restrictedText`} />
      <Text variant="body2" sx={{ marginTop: '8px' }}>
        and include the required disclaimer:
      </Text>
      <ControlledInput placeholder="Enter disclaimer" name={`parameters.disclaimer`} />
    </ConditionsWrapper>
  );
};

interface AvoidRestrictedTextForClaimsProps {
  index: number;
}

const AvoidRestrictedTextForClaims: React.FC<AvoidRestrictedTextForClaimsProps> = ({ index }) => {
  return (
    <ConditionsWrapper>
      <Text variant="body2">For all selected products, avoid making claims about:</Text>
      <ControlledInput placeholder="Enter custom text" name={`parameters.topic`} />
      <Text variant="body2" sx={{ marginTop: '8px' }}>
        that reference:
      </Text>
      <ControlledInput placeholder="Enter restricted text" name={`parameters.referenceText`} />
    </ConditionsWrapper>
  );
};

interface ModalSectionProps {
  index: number;
}

const ModalSection: React.FC<ModalSectionProps> = ({ index }) => {
  const { control } = useFormContext();

  // Watch the type field to determine which section to render
  const type = useWatch({
    control,
    name: 'type'
  });

  switch (type) {
    case BRAND_NAME_FORMATTING:
      return <BrandNameFormatting index={index} />;
    case APPROVED_SUBSTITUTIONS:
      return <ApprovedSubstitutions index={index} />;
    case AVOID_CROSS_CATEGORY_COMPARISONS:
      return <AvoidCrossCategoryComparisons index={index} />;
    case APPROVED_NUTRIENT_RELATED_PHRASES:
      return <ApprovedNutrientRelatedPhrases index={index} />;
    case AVOID_SPECIFIC_CLAIM_LANGUAGE:
      return <AvoidSpecificClaimLanguage index={index} />;
    case APPROVED_ON_PACKAGE_LANGUAGE:
      return <ApprovedOnPackageLanguage index={index} />;
    case APPROVED_QUALIFIER_PHRASES_BEFORE_CLAIMS:
      return <ApprovedQualifierPhrasesBeforeClaims index={index} />;
    case APPROVED_PRODUCT_ATTRIBUTE_AND_DISCLAIMER_TEXT:
      return <ApprovedProductAttributeAndDisclaimerText index={index} />;
    case AVOID_RESTRICTED_TEXT_FOR_CLAIMS:
      return <AvoidRestrictedTextForClaims index={index} />;
    default:
      return null;
  }
};

export default ModalSection;
